import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, List, Space, Tag, Typography, message, Empty } from 'antd';
import { CheckOutlined, CloseOutlined, ShoppingOutlined } from '@ant-design/icons';
import { orderCorrectionsAPI } from '../services/api';

const { Text } = Typography;

const CORRECTION_TYPE_LABEL: Record<string, string> = {
  missing: 'Не довезли',
  wrong_item: 'Другое блюдо',
  quantity: 'Неверное количество',
  quality: 'Качество',
  other: 'Другое'
};

interface PendingOrderCorrectionsCardProps {
  /** Показывать заказчику и контролёру */
  enabled: boolean;
}

const PendingOrderCorrectionsCard: React.FC<PendingOrderCorrectionsCardProps> = ({ enabled }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [items, setItems] = React.useState<any[]>([]);

  const load = React.useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const data = await orderCorrectionsAPI.listPending();
      setItems(Array.isArray(data) ? data : []);
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Не удалось загрузить корректировки');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  React.useEffect(() => {
    load();
  }, [load]);

  const handleApprove = async (id: number) => {
    try {
      await orderCorrectionsAPI.approve(id);
      message.success('Корректировка подтверждена');
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Не удалось подтвердить');
    }
  };

  const handleReject = async (id: number) => {
    try {
      await orderCorrectionsAPI.reject(id, 'Отклонено');
      message.success('Корректировка отклонена');
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Не удалось отклонить');
    }
  };

  if (!enabled) return null;

  return (
    <Card
      title={
        <Space>
          <ShoppingOutlined />
          Корректировки по расхождениям
          {items.length > 0 ? <Tag color="orange">{items.length} на подтверждении</Tag> : null}
        </Space>
      }
      style={{ marginTop: 16 }}
      loading={loading}
      extra={
        <Button size="small" onClick={load}>
          Обновить
        </Button>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Подтверждение корректировок суммы"
        description="Поставщик или контролёр предлагает уменьшить сумму заказа после расхождения. Подтвердите или отклоните предложение."
      />

      {items.length === 0 ? (
        <Empty description="Нет корректировок, ожидающих подтверждения" />
      ) : (
        <List
          dataSource={items}
          renderItem={(c: any) => {
            const typeLabel = CORRECTION_TYPE_LABEL[c.discrepancyType] || c.discrepancyType || '—';
            const subjectParts: string[] = [];
            if (c.subjectEmployee?.fullName || c.subjectEmployee?.username) {
              subjectParts.push(c.subjectEmployee.fullName || c.subjectEmployee.username);
            }
            if (c.subjectMenuItem?.name) subjectParts.push(c.subjectMenuItem.name);

            return (
              <List.Item
                actions={[
                  <Button
                    key="approve"
                    type="primary"
                    size="small"
                    icon={<CheckOutlined />}
                    onClick={() => handleApprove(c.id)}
                  >
                    Подтвердить
                  </Button>,
                  <Button
                    key="reject"
                    size="small"
                    danger
                    icon={<CloseOutlined />}
                    onClick={() => handleReject(c.id)}
                  >
                    Отклонить
                  </Button>,
                  <Button key="open" size="small" type="link" onClick={() => navigate('/orders')}>
                    К заказам
                  </Button>
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space wrap>
                      <Text strong>Заказ №{c.order?.id}</Text>
                      <Text>
                        {c.originalTotalAmount} ₽ → {c.correctedTotalAmount} ₽ ({c.correctionAmount} ₽)
                      </Text>
                    </Space>
                  }
                  description={
                    <div>
                      <div>
                        <Text type="secondary">Тип: </Text>
                        {typeLabel}
                      </div>
                      {subjectParts.length > 0 && (
                        <div>
                          <Text type="secondary">По: </Text>
                          {subjectParts.join(' · ')}
                        </div>
                      )}
                      <div>
                        <Text type="secondary">Причина: </Text>
                        {c.reason}
                      </div>
                      <div>
                        <Text type="secondary">Предложил: </Text>
                        {c.proposedBy?.fullName || c.proposedBy?.username}
                      </div>
                    </div>
                  }
                />
              </List.Item>
            );
          }}
        />
      )}
    </Card>
  );
};

export default PendingOrderCorrectionsCard;
